import {
  Card, Title, Text,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
  BarChart,
} from '@tremor/react';
import { Link } from 'react-router-dom';
import type { CustomerRecentTicket, CustomerTopPart, CustomerMonthBucket } from '../api/types';
import { t } from '../lib/i18n';

const mxn = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

interface Props {
  recentTickets: CustomerRecentTicket[];
  topParts: CustomerTopPart[];
  revenueTrend: CustomerMonthBucket[];
  onTicketClick: (sid: number, tnum: number) => void;
}

export default function CustomerActivitySection({
  recentTickets, topParts, revenueTrend, onTicketClick,
}: Props) {
  return (
    <div className="space-y-4">
      <Card>
        <Title>{t.customer.revenueTrend.title}</Title>
        {revenueTrend.length === 0 ? (
          <Text className="mt-2">{t.customer.revenueTrend.empty}</Text>
        ) : (
          <BarChart
            className="mt-4 h-48"
            data={revenueTrend.map((m) => ({
              month: m.month,
              [t.customer.revenueTrend.title]: m.revenue_mxn,
            }))}
            index="month"
            categories={[t.customer.revenueTrend.title]}
            valueFormatter={mxn}
            showLegend={false}
          />
        )}
      </Card>

      <Card>
        <Title>{t.customer.recentTickets.title}</Title>
        {recentTickets.length === 0 ? (
          <Text className="mt-2">{t.customer.recentTickets.empty}</Text>
        ) : (
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.customer.recentTickets.cols.idate}</TableHeaderCell>
                <TableHeaderCell>{t.customer.recentTickets.cols.tnum}</TableHeaderCell>
                <TableHeaderCell>{t.customer.recentTickets.cols.type}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.recentTickets.cols.total}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.recentTickets.cols.balance}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTickets.map((tk) => (
                <TableRow
                  key={`${tk.sid}-${tk.tnum}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => onTicketClick(tk.sid, tk.tnum)}
                >
                  <TableCell>{tk.idate}</TableCell>
                  <TableCell>{tk.tnum}</TableCell>
                  <TableCell>{tk.type}</TableCell>
                  <TableCell className="text-right">{mxn(tk.total_mxn)}</TableCell>
                  <TableCell className="text-right">
                    {tk.balance_mxn === null ? '—' : mxn(tk.balance_mxn)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <Title>{t.customer.topParts.title}</Title>
        {topParts.length === 0 ? (
          <Text className="mt-2">{t.customer.topParts.empty}</Text>
        ) : (
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.customer.topParts.cols.part}</TableHeaderCell>
                <TableHeaderCell>{t.customer.topParts.cols.pdesc}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.topParts.cols.qty}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.topParts.cols.revenue}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.topParts.cols.margin}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topParts.map((p) => (
                <TableRow key={`${p.line}-${p.part}`}>
                  <TableCell>
                    <Link to={`/parts/${encodeURIComponent(p.line)}/${encodeURIComponent(p.part)}`} className="text-tremor-brand hover:underline">
                      {p.part}
                    </Link>
                  </TableCell>
                  <TableCell>{p.pdesc}</TableCell>
                  <TableCell className="text-right">{p.qty_sold}</TableCell>
                  <TableCell className="text-right">{mxn(p.revenue_mxn)}</TableCell>
                  <TableCell className="text-right">{mxn(p.margin_mxn)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
